import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import type { Size } from "@/components/helpers/common-props";
import type { FlexComponent } from "@/utils/render-component";
import { computed, defineComponent, reactive, Transition } from "vue";
import { useCtx } from "@/composables/context";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import { renderComponent } from "@/utils/render-component";
import Label, { labelProps } from "../label/Label";

const props = {
  id: p.string(() => {
    const suffix = Math.random().toString(36).slice(2, 11);
    return "field-" + suffix;
  }),
  label: p.string(),
  name: p.string(),
  errorMessage: p.string(),
  warningMessage: p.string(),
  required: p.boolean(),
  tooltip: p.string(),
  hint: p.string(),
  horizontal: p.boolean(),
  hideRequiredMark: p.boolean(),
  size: p.string<Size>("md"),
  prefix: p.custom<FlexComponent>([String, Object]),
  suffix: p.custom<FlexComponent>([String, Object]),
  hideFooter: p.boolean(),
  info: labelProps.info,
  hintPosition: p.string<"top" | "bottom">("bottom"),
  inline: p.boolean(),
  labelClass: p.string(),
} satisfies ComponentPropsOptions;

type FieldProps = ExtractPublicPropTypes<typeof props>;

export const fieldCtx = useCtx<FieldProps>();

export default defineComponent({
  name: "Field",
  props,
  setup(props, { slots }) {
    const showRequired = computed(() => {
      return props.required && !props.hideRequiredMark;
    });

    fieldCtx.provide(reactive(props));

    const Hint = () => (
      <small
        id={`${props.id}-hint`}
        class="text-muted-foreground transition-all duration-500 animate-in slide-in-from-top"
      >
        {props.hint}
      </small>
    );

    return () =>
      props.horizontal ? (
        <div class="flex flex-row items-start space-x-4">
          <div class={cn("flex-none", props.labelClass)}>
            <Label
              label={props.label}
              id={props.id}
              required={showRequired.value}
              tooltip={props.tooltip}
              info={props.info}
            />
          </div>
          <div class="flex-auto">
            {props.hint && props.hintPosition === "top" && (
              <div class="mb-1">
                <Hint />
              </div>
            )}
            <div class="my-1">{slots.default?.()}</div>
            <div class="flex justify-between">
              <div v-show={!props.hideFooter} class="min-h-6 px-1 text-xs">
                {props.hint && props.hintPosition === "bottom" && !props.errorMessage && (
                  <div class="mt-1">
                    <Hint />
                  </div>
                )}
                <Transition
                  enter-active-class="transition ease-out duration-500"
                  enter-from-class="transform opacity-0 translate-y-1"
                  enter-to-class="transform opacity-100 translate-y-0"
                  leave-active-class="transition ease-out duration-500"
                  leave-from-class="transform opacity-100 translate-y-0"
                  leave-to-class="transform opacity-0 translate-y-1"
                >
                  {props.errorMessage && (
                    <span
                      id={`${props.id}-error`}
                      class="text-red-11 transition-all"
                      role="alert"
                      aria-live="assertive"
                    >
                      {props.errorMessage}
                    </span>
                  )}
                  {props.warningMessage && (
                    <span
                      id={`${props.id}-warning`}
                      class="text-amber-11 transition-all"
                      role="alert"
                      aria-live="assertive"
                    >
                      {props.warningMessage}
                    </span>
                  )}
                </Transition>
              </div>
              {slots.$right?.()}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div class="relative flex items-end justify-between gap-2">
            <div class="flex gap-2">
              {props.prefix && <div class="shrink-0">{renderComponent(props.prefix)}</div>}
              <Label
                class={cn(
                  props.inline &&
                    "leading-0 absolute z-20 translate-x-3 translate-y-[12px] px-1 py-0 text-[0.6rem] text-muted-foreground before:absolute before:inset-0 before:-z-10 before:rounded-sm before:bg-linear-to-r before:from-card/90 before:via-card/95 before:to-card/90"
                )}
                label={props.label}
                id={props.id}
                required={showRequired.value}
                tooltip={props.tooltip}
                info={props.info}
              />
            </div>
            {props.suffix && renderComponent(props.suffix)}
          </div>
          {props.hintPosition === "top" && (
            <div class="mb-3 px-1 leading-tight">
              <Hint />
            </div>
          )}
          <div class="my-1 grow">{slots.default?.()}</div>
          <div class="flex justify-between">
            <div v-show={!props.hideFooter} class="min-h-6 px-1 text-xs">
              {props.hintPosition === "bottom" && props.hint && !props.errorMessage && <Hint />}
              <Transition
                enter-active-class="transition ease-out duration-500"
                enter-from-class="transform opacity-0 translate-y-1"
                enter-to-class="transform opacity-100 translate-y-0"
                leave-active-class="transition ease-out duration-500"
                leave-from-class="transform opacity-100 translate-y-0"
                leave-to-class="transform opacity-0 translate-y-1"
              >
                {props.errorMessage && (
                  <span
                    id={`${props.id}-error`}
                    class="text-red-11 transition-all"
                    role="alert"
                    aria-live="assertive"
                  >
                    {props.errorMessage}
                  </span>
                )}
                {props.warningMessage && (
                  <span
                    id={`${props.id}-warning`}
                    class="text-amber-11 transition-all"
                    role="alert"
                    aria-live="assertive"
                  >
                    {props.warningMessage}
                  </span>
                )}
              </Transition>
            </div>
            {slots.$right?.()}
          </div>
        </div>
      );
  },
});
